import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import moment from '@g4/moment-utc';
import { useMutation } from '@apollo/react-hooks';
import useBookingSteps from '../hooks/useBookingSteps';
import useLastCompletedBookingStep from '../hooks/useLastCompletedBookingStep';
import {
  withTranslations,
  useTranslations,
} from './../graphql/components/TranslationsProvider';
import get from 'lodash/get';
import tracking from '@g4/g4-analytics';
import { clearPaypalFromSession } from '@g4/next-furniture/utils/paypalUtils';

import getFlightsRawProducts from './indexPage/getFlightsRawProducts';
import getBundleRawProducts, { getBundleRawProductsForFlight, getRawProductsWithIndexAndItems } from './bundlesPage/getBundleRawProducts';
import NoFlightsErrorMessage from '../pageComponents/indexPage/NoFlightsErrorMessage';
import { DEPARTING, RETURNING, tripTypeValues, eventTypes } from './indexPage/constants';
import useFlightSearchCriteria from '../hooks/useFlightSearchCriteria';
import useErrorHandling from '../hooks/useErrorHandling';
import { useMedia, VerticalSpacing, Box } from '../components/Boneyard';
import Disclaimer from '../components/Disclaimer';
import PageFooter from '../components/PageFooter';
import FlightsPromoBanner from '../components/FlightsPromoBanner';
import ExpiredTransactionIdErrorPage from '../components/ExpiredTransactionIdErrorPage';
import SpinnerOverlay from '../components/SpinnerOverlay';
import FlightsPageHeader from './indexPage/FlightsPageHeader';
import SearchResults from './indexPage/SearchResults';
import useSearchCriteria from './indexPage/useSearchCriteria';
import { useError, useWarning } from './indexPage/searchResults/validation';
import useSelectedFlightOptions from './indexPage/useSelectedFlightOptions';
import useTabDates from './indexPage/useTabDates';
import DestinationAdvert from '../components/DestinationAdvert';

import { useSSRQuery, useBookingPath } from './../graphql';
import { useAuthentication, useCart, useSeatSizesMap } from '../graphql';
import FLIGHTS_PAGE_QUERY from './indexPage/flightsPage.query.graphql.js';
import FLIGHTS_PAGE_TRANSLATIONS_QUERY from './indexPage/flightsPageTranslations.query.graphql.js';
import CONTINUE_BUTTON_MUTATION from './indexPage/continueButton.mutation.graphql.js';
import REMOVE_ORDER_ITEMS from './indexPage/removeOrderItems.mutation.graphql.js';
import GOOGLE_ANALYTICS_QUERY from './googleAnalyticsData.query.graphql.js';
import useMarketQuery from './indexPage/useMarketQuery';
import useFlightsQueries from './indexPage/searchResults/useFlightsQueries';
import { isActiveLoyaltyUser } from '../utils/loyaltyHelpers';
import {
  FLIGHTS_PAGE,
  FLIGHTS_PAGE_SPINNER,
  FLIGHTS_DISCLAIMER,
  FLIGHTS_DISCLAIMER_BUTTON,
  FLIGHTS_DISCLAIMER_POPUP,
  FLIGHTS_DISCLAIMER_EXTERNAL_LINK,
} from './../../automationHooks.json';
import Markdown from '../components/Markdown';
import ErrorPopup from '../components/ErrorPopup';
import useLazySelectStadiumShows from './indexPage/useLazySelectStadiumShow';
import useStadiumGame from '../hooks/useStadiumGame';
import usePersistedOrderOptions from './indexPage/usePersistedOrderOptions';
import useRoundTripConverter from './indexPage/useRoundTripConverter';
import { getAffinityString } from './app/trackingItems.js';
import { sortOrder } from '../utils/constants/trackingConstants.js';
import { hasTargetingConsent } from '../utils/ga4Utils';
import useBundleSelection from '../pageComponents/indexPage/searchResults/useBundleSelection.js';
import { NEW_FARE_TIER, PREMIUM_FARE_NAME, DEFAULT_FARE_NAME } from './indexPage/constants.js';

const propTypes = {
  flightSearchCriteria: PropTypes.object,
  invalidateFlightSearchCriteriaCache: PropTypes.func,
};

const IndexPage = ({
  flightSearchCriteria,
  invalidateFlightSearchCriteriaCache,
}) => {
  const mq = useMedia();
  const { navigateToNextStep, isChangingRoute } = useBookingSteps();
  const { setLastStepCompleted } = useLastCompletedBookingStep();
  const { isStadium, setTabDates, tabDates, backupTabDate, setBackupTabDate } =
    useBookingPath();
  const [stadiumGame] = useStadiumGame();
  const { cart, updateCart } = useCart();
  const { updateSeatSizesMap } = useSeatSizesMap();
  const { isAuthenticated, showUserLoyaltyFareBanner } = useAuthentication();
  
  tracking.setDataLayerVars({
    content_group: 'FlightsPage',
    customer_type: isAuthenticated ? 'Customer' : 'Guest',
  });

  const {
    common: { taxesChargesFeesPopupContent },
    flightsPage: {
      flightsPageDisclaimerText,
      flightsPageDisclaimerPopupWindowTitle,
      missingFlightsErrorTitle,
      missingFlightsErrorMessage,
      loyaltyFareDiscountHeading,
    },
  } = useTranslations();

  //used for calendar view selected date,
  //which it should not be displayed as selected if the user didn't saw the list view of selected date
  const [displayReturningSelectedDate, setDisplayReturningSelectedDate] =
    useState(false);

  const setDisplayCalendarViewSelectedDate = useCallback(
    (display) => {
      setDisplayReturningSelectedDate(display);
    },
    [setDisplayReturningSelectedDate]
  );

  const { searchCriteria, setSearchCriteria } =
    useSearchCriteria(flightSearchCriteria);

  // Get the last available dates for both legs by executing the query
  const { earliestAvailableDates, lastAvailableDates, ...marketQuery } =
    useMarketQuery(searchCriteria);

  // Controls the 'selected' and 'middle' dates for each leg.
  // The 'selected' date displays the flights for that day.
  // The 'middle' date controls which date-tabs are available for selection.
  // Changes in the 'selected' dates will trigger flight-shop queries for that date.
  // Changes in the 'middle' dates will trigger low-fare queries for a computed date-range.
  const {
    updateTabDates, // used for updating departing & returning dates simultaneously.
    setDepartingTabs,
    setReturningTabs,
  } = useTabDates(searchCriteria, tabDates, setTabDates);

  // Executes the flights search query
  const {
    isOnewayWithRoundTripDiscount,
    onewayToRoundtripSavedAmount,
    roundtripDiscountValue,
    [DEPARTING]: departingQuery,
    [RETURNING]: returningQuery,
    flightsQueryError,
    loyaltyFare,
    ...flightsQuery
  } = useFlightsQueries(searchCriteria, tabDates);

  // Extract bundleOffers from flights data
  const bundleOffers = get(flightsQuery, 'data.flights.bundleOffers', []);
  const bundleOffersList = bundleOffers.list || [];
  const allAncillariesList = bundleOffers.allAncillariesList || [];

  const { makeOneway, makeRoundtrip } = useRoundTripConverter(
    tabDates,
    setDepartingTabs,
    setReturningTabs,
    searchCriteria,
    setSearchCriteria,
    flightsQuery
  );

  const {
    warning,
    deactivateWarning,
    setDepartingTabsAndValidate,
    setReturningTabsAndClearWarning,
    shouldReturnFlightOptionBeNull,
    setShouldReturnFlightOptionBeNull,
  } = useWarning(
    searchCriteria.tripType,
    tabDates,
    updateTabDates,
    lastAvailableDates,
    setDepartingTabs,
    setReturningTabs,
    stadiumGame,
    departingQuery
  );

  const { persistedFlightOptions, orderItemIds, isPersistedShow } =
    usePersistedOrderOptions(flightsQuery);

  const [isSelectFlightError, setIsSelectFlightError] = useState(false);
  const [isPageViewTriggered, setIsPageViewTriggered] = useState(false);

  const flightsPageQueryVariables = {
    origin: searchCriteria.origin,
    destination: searchCriteria.destination,
    departureDate: searchCriteria.departDate?.date,
    returningDate: searchCriteria.returnDate?.date,
    skipUserInfo: !isAuthenticated,
  };

  const { data, loading: userDataLoading } = useSSRQuery(FLIGHTS_PAGE_QUERY, {
    fetchPolicy: 'no-cache',
    variables: flightsPageQueryVariables,
  });

  const allwaysAccountNumber = data?.viewer?.loyaltyId;
    tracking.setDataLayerVars({
    allways_account_no:
      isAuthenticated && hasTargetingConsent() ? allwaysAccountNumber : '',
  });

  const loyalty = data?.viewer?.loyalty;

  const loyaltySettings = data?.settings?.loyalty;

  const getLoyaltyPointsValue = () => {
    if (loyalty) {
      const { status, pointsValue } = loyalty;
      if (!isActiveLoyaltyUser(status) || pointsValue < 0) {
        return 0;
      }
      tracking.setDataLayerVars({ loyalty_points_total: pointsValue * 100 });
      return pointsValue;
    }

    return 0;
  };

  const googleAnalyticsQueryVariables = {
    criteria: {
      market: searchCriteria?.origin + searchCriteria?.destination,
      tripType: searchCriteria?.tripType,
    },
  };

  const googleAnalyticsData = useSSRQuery(GOOGLE_ANALYTICS_QUERY, {
    fetchPolicy: 'cache-first',
    variables: googleAnalyticsQueryVariables,
  });

  const flightsPageDestinationAdvert =
    data?.application.destinationAdverts.find(
      (item) => item.__typename === 'DestinationAdvert'
    );

  //error handling for redirects to flights page after FARES_CHANGE, FLIGHT_UNAVAILABLE
  const [recoverableError, { clearErrorMessages }] = useErrorHandling();
  const [handlingError, setHandlingError] = React.useState(false);

  const [removeOrderItems] = useMutation(REMOVE_ORDER_ITEMS);

  React.useEffect(() => {
    const cleanup = async () => {
      await removeOrderItems({
        variables: {
          orderItemIds: orderItemIds,
        },
      });
      clearErrorMessages('FLIGHT_UNAVAILABLE');
      clearErrorMessages('FARES_CHANGED');
      setHandlingError(false);
    };

    if (recoverableError && !handlingError && orderItemIds) {
      setHandlingError(true);
      if (
        recoverableError.graphqlErrors[0] === 'FLIGHT_UNAVAILABLE' ||
        recoverableError.graphqlErrors[0] === 'FARES_CHANGED'
      ) {
        cleanup();
      }
    }
  }, [
    clearErrorMessages,
    handlingError,
    orderItemIds,
    recoverableError,
    removeOrderItems,
  ]);

  const isRoundTrip =
    flightSearchCriteria.tripType === tripTypeValues.ROUNDTRIP;

  const {
    selectedFlightOptions,
    setDepartingFlightOption,
    setReturningFlightOption,
    setIsArrowClicked,
    // Ref containing the leg where the latest Flight Option selection was made.
    updatedLeg,
  } = useSelectedFlightOptions(persistedFlightOptions);

  const { selectedBundles, handleBundleSelect, cartBundleSelections } =
    useBundleSelection(isStadium);

  const getFareNameByLeg = useCallback(
    (leg) => {
      const selectedFareName = selectedFlightOptions[leg]?.selectedFareName;
      const selectedTier =
        leg === DEPARTING
          ? selectedBundles.outboundTier
          : selectedBundles.inboundTier;

      return (
        selectedFareName === PREMIUM_FARE_NAME ||
        selectedTier === NEW_FARE_TIER
      )
        ? PREMIUM_FARE_NAME
        : DEFAULT_FARE_NAME;
    },
    [selectedFlightOptions, selectedBundles]
  );

  const makeRoundtripFunction = useCallback(() => {
    makeRoundtrip(selectedFlightOptions?.[DEPARTING]?.incentiveReturnDate);
  }, [selectedFlightOptions, makeRoundtrip]);

  const error = useError(
    searchCriteria.tripType,
    selectedFlightOptions,
    updatedLeg
  );

  const { adultsCount, childrenCount, infantCount } = searchCriteria;
  const totalPassengerCount = adultsCount + childrenCount + infantCount;

  //When modify flight search we have to update the transactionId, then make the checkout track
  const router = useRouter();
  const { transactionId } = router.query;
  tracking.setDataLayerVars({ search_manifest: transactionId });

  const {
    selectStadiumShows,
    selectStadiumShowsSuccess,
    selectStadiumShowsError,
  } = useLazySelectStadiumShows();

  // This state gets updated when a new transaction id is returned when selecting a flight
  // This happens when the users returns to the flights page and modifies a flight date
  // or chooses a new flight
  const [updateTransactionIdInRoute, setUpdateTransactionIdInRoute] =
    useState(null);

  const continueMutationVariablesRef = useRef(null);

  const dataLayerVars = tracking.getDataLayerVars();
  const googleAnalyticsClientId = dataLayerVars.customer_id;

  const cartItems = useMemo(() => {
    if (!cart?.order?.items?.length) return { flights: [], bundles: [] };

    const flights = [];
    const bundles = [];

    cart.order.items.forEach((item) => {
      if (item.__typename === 'FlightOrderItem' && item.flight) {
        flights.push(item);
      } else if (item.__typename === 'BundleOrderItem' && item.bundle) {
        bundles.push(item);
      }
    });

    return { flights, bundles };
  }, [cart?.order?.items]);

  //Function to get previous flight options
  const getPreviousFlightOptions = useCallback(() => {
    if (!cartItems.flights.length) return { flights: {} };

    const cartFlightOptions = {};

    cartItems.flights.forEach((item) => {
      const leg =
        item.flight.origin?.code === searchCriteria.origin
          ? DEPARTING
          : RETURNING;
      cartFlightOptions[leg] = {
        flight: item.flight,
        id: item.flight.id,
        price: item.price?.amount || 0,
        totalDiscountValue: item.totalDiscountValue || 0,
        discountType: item.discountType || '',
      };
    });

    return { flights: cartFlightOptions };
  }, [cartItems.flights, searchCriteria.origin]);

  // Flight and bundle change detection
  const getChanges = useCallback(() => {
    const flightChanges = {
      departing: !selectedFlightEqualsToPersistedFlight(DEPARTING),
      returning: !selectedFlightEqualsToPersistedFlight(RETURNING),
    };

    const bundleChanges = {
      departing:
        selectedBundles.outboundTier !== cartBundleSelections.outboundTier ||
        (selectedBundles.outboundTier && flightChanges.departing),
      returning:
        selectedBundles.inboundTier !== cartBundleSelections.inboundTier ||
        (selectedBundles.inboundTier && flightChanges.returning),
    };

    return { flightChanges, bundleChanges };
  }, [
    selectedFlightOptions,
    persistedFlightOptions,
    selectedBundles,
    cartBundleSelections,
  ]);

  // Bundle removal tracking
  const trackBundleRemovals = useCallback(
    (bundleChanges, affinityString) => {
      if (
        !cartItems.bundles.length ||
        (!bundleChanges.departing && !bundleChanges.returning)
      ) {
        return [];
      }

      const removedBundleProducts = [];

      const flightMap = new Map(
        cartItems.flights.map((item) => [item.flight.id, item])
      );

      cartItems.bundles.forEach((bundleItem) => {
        const correspondingFlight = flightMap.get(bundleItem.flightId);
        if (!correspondingFlight) return;

        const flightLeg =
          correspondingFlight.flight.origin?.code === searchCriteria.origin
            ? DEPARTING
            : RETURNING;

        const shouldProcessBundle =
          bundleChanges[flightLeg === DEPARTING ? 'departing' : 'returning'];
        if (!shouldProcessBundle) return;

        const matchingBundleOffer = bundleOffersList.find(
          (offer) =>
            offer.id === bundleItem.bundle.id ||
            offer.name === bundleItem.bundle.name
        );

        if (matchingBundleOffer) {
          const bundleRawProducts = getRawProductsWithIndexAndItems(
            getBundleRawProductsForFlight(
              matchingBundleOffer,
              searchCriteria,
              affinityString,
              correspondingFlight.flight
            )
          );
          removedBundleProducts.push(...bundleRawProducts);
        }
      });

      return removedBundleProducts;
    },
    [
      cartItems.bundles,
      cartItems.flights,
      searchCriteria.origin,
      bundleOffersList,
      cart,
    ]
  );

  // Helper function to track flight removals
  const trackFlightRemovals = useCallback(
    (
      flightChanges,
      previousFlightOptions,
      loyaltyPointsDiscount,
      affinityString,
      googleAnalyticsFlightMarketData
    ) => {
      // Early return if no changes
      if (!flightChanges.departing && !flightChanges.returning) {
        return [];
      }

      const changedLegsFlightOptions = {};

      if (flightChanges.departing && previousFlightOptions.departing) {
        changedLegsFlightOptions.departing = previousFlightOptions.departing;
      }

      if (flightChanges.returning && previousFlightOptions.returning) {
        changedLegsFlightOptions.returning = previousFlightOptions.returning;
      }

      // Return empty array if no valid changes
      if (!Object.keys(changedLegsFlightOptions).length) {
        return [];
      }

      return getFlightsRawProducts(
        changedLegsFlightOptions,
        searchCriteria,
        loyaltyPointsDiscount,
        affinityString,
        googleAnalyticsFlightMarketData,
        undefined,
        eventTypes.REMOVE_FROM_CART,
        getFareNameByLeg(DEPARTING),
        getFareNameByLeg(RETURNING)
      );
    },
    [searchCriteria]
  );

  // Setup the continue button mutation
  const [
    submitFlights,
    { loading: isContinueLoading, data: submitFlightsData },
  ] = useMutation(CONTINUE_BUTTON_MUTATION, {
    context: {
      headers: {
        'google-analytics-client-id': googleAnalyticsClientId,
      },
    },
    onCompleted: async (data) => {
      const continueMutationVariables = continueMutationVariablesRef.current;
      const totalPrice = data?.selectFlights?.order?.price?.total || 0;
      const loyaltyPointsDiscount = loyaltyFare?.discount;
      const affinityString = getAffinityString(cart?.order);
      const googleAnalyticsFlightMarketData =
        googleAnalyticsData?.data?.application?.googleAnalytics?.flightMarkets;
      const departureFareName = continueMutationVariables?.departureFareName;
      const returningFareName = continueMutationVariables?.returningFareName;

      const selectedFlightsRawProducts = getFlightsRawProducts(
        selectedFlightOptions,
        searchCriteria,
        loyaltyPointsDiscount,
        affinityString,
        googleAnalyticsFlightMarketData,
        undefined,
        eventTypes.ADD_TO_CART,
        departureFareName,
        returningFareName
      );

      const selectdbundlesRawProducts = getBundleRawProducts(
        selectedFlightOptions,
        selectedBundles,
        bundleOffersList,
        searchCriteria,
        affinityString
      );

      const selectedRawProducts = [
        ...selectedFlightsRawProducts,
        ...selectdbundlesRawProducts,
      ];

      // Get previous flight options and track changes
      const previousFlightOptions = getPreviousFlightOptions().flights;
      const { flightChanges, bundleChanges } = getChanges();

      let removedProducts = [];
      let removedTotalValue = 0;

      // Track flight removals
      if (flightChanges.departing || flightChanges.returning) {
        const prevFlightRawProducts = trackFlightRemovals(
          flightChanges,
          previousFlightOptions,
          loyaltyPointsDiscount,
          affinityString,
          googleAnalyticsFlightMarketData
        );
        removedProducts = removedProducts.concat(prevFlightRawProducts);
        removedTotalValue += prevFlightRawProducts.reduce(
          (sum, product) => sum + (product.price || 0),
          0
        );
      }

      // Track bundle removals
      if (bundleChanges.departing || bundleChanges.returning) {
        const prevBundleRawProducts = trackBundleRemovals(
          bundleChanges,
          affinityString
        );
        removedProducts = removedProducts.concat(prevBundleRawProducts);
        removedTotalValue += prevBundleRawProducts.reduce(
          (sum, product) => sum + (product.price || 0),
          0
        );
      }

      // Track removals if there are any
      if (removedProducts.length > 0) {
        const removeImpressionData = {
          event: 'remove_from_cart',
          currency: 'USD',
          value: removedTotalValue,
          products: removedProducts,
        };
        tracking.removeFromCart(removeImpressionData);
      }

      const impressionData = {
        event: 'add_to_cart',
        currency: 'USD',
        value: totalPrice,
        name: sortOrder.DEFAULT,
        products: selectedRawProducts,
      };

      const checkoutImpressionData = {
        event: 'begin_checkout',
        currency: 'USD',
        price: totalPrice,
        products: selectedRawProducts,
      };

      const { transactionId, ...query } = router.query;
      const hasTransactionIdChanged =
        transactionId !== data?.selectFlights?.transactionId;

      // update route if transaction id changed during flight reselection
      if (hasTransactionIdChanged) {
        // clear the paypal token when transaction id changed
        clearPaypalFromSession();
        tracking.setDataLayerVars({
          search_manifest: data?.selectFlights?.transactionId,
        });
        router.push(
          {
            pathname: `/booking/${data?.selectFlights?.transactionId}/flights`,
            query: query,
          },
          undefined,
          { shallow: true }
        );
        setUpdateTransactionIdInRoute(data?.selectFlights?.transactionId);
        tracking.setDataLayerVars({
          search_manifest: data?.selectFlights?.transactionId,
        });
      } else {
        if (isStadium) {
          selectStadiumShows({
            travelers: data?.selectFlights?.order?.travelers,
          });
        } else {
          if (
            data?.selectFlights?.errors &&
            data?.selectFlights?.errors[0] === 'UNKNOWN_ERROR' &&
            data?.selectFlights?.order === null
          ) {
            setIsSelectFlightError(true);
          } else {
            setIsSelectFlightError(false);
            await updateCart();
            updateSeatSizesMap();
            navigateToNextStep();
            setLastStepCompleted('FLIGHTS');
          }
        }
      }
      if (
        searchCriteria.departDate.date !== tabDates.departing.selected ||
        searchCriteria?.returnDate?.date !== tabDates?.returning?.selected
      ) {
        tracking.trackAddToCartFlightChange({
          price: totalPrice * totalPassengerCount,
          viewType: 'LIST_VIEW',
        });
      } 
      tracking.addToCart(impressionData);
      tracking.checkout(checkoutImpressionData);
    },
  });

  // Listen on the updateTransactionIdInRoute, and perform post flight select actions
  useEffect(() => {
    if (
      updateTransactionIdInRoute &&
      !isChangingRoute &&
      transactionId === updateTransactionIdInRoute
    ) {
      if (isStadium && !selectStadiumShowsSuccess) {
        // show stadium selection needs to be run here because submit flights
        // and select show mutation happen right after one another.
        // If the transaction id has changed select show mutation
        // needs to use the new transaction id and has to wait,
        // till it is updated in the router query

        selectStadiumShows({
          travelers: submitFlightsData?.selectFlights?.order?.travelers,
        });

        //  If the booking is stadium booking, wait for the
        //  selectStadiumShowsSuccess to be true before calling postSuccessSteps
        return;
      }

      const postSuccessSteps = async () => {
        await updateCart();
        updateSeatSizesMap();
        navigateToNextStep();
        setLastStepCompleted('FLIGHTS');
      };

      postSuccessSteps();
    }
  }, [
    updateTransactionIdInRoute,
    updateCart,
    updateSeatSizesMap,
    navigateToNextStep,
    setLastStepCompleted,
    isChangingRoute,
    transactionId,
    isStadium,
    selectStadiumShows,
    selectStadiumShowsSuccess,
    submitFlightsData,
  ]);

  useEffect(() => {
    const postStadiumSuccessSteps = async () => {
      await updateCart();
      navigateToNextStep();
      setLastStepCompleted('FLIGHTS');
    };

    //  Check if transaction id has changed or not.
    //  If it has changed then skip navigating to next step as there
    //  is already an useEffect for handling transaction id change
    if (selectStadiumShowsSuccess && !updateTransactionIdInRoute) {
      postStadiumSuccessSteps();
    }
  }, [
    selectStadiumShowsSuccess,
    navigateToNextStep,
    setLastStepCompleted,
    updateCart,
    updateTransactionIdInRoute,
  ]);

  const selectedFlightEqualsToPersistedFlight = (leg) => {
    return (
      persistedFlightOptions[leg] === selectedFlightOptions[leg] ||
      (!persistedFlightOptions[leg] && !selectedFlightOptions[leg])
    );
  };

  const selectedBundlesEqualToPersistedBundles = () => {
    return (
      selectedBundles.outboundTier === cartBundleSelections.outboundTier &&
      selectedBundles.inboundTier === cartBundleSelections.inboundTier
    );
  };

  const disableFlightIfSameDay = moment(
    selectedFlightOptions[RETURNING]?.flight.departingTime
  ).isBefore(moment(selectedFlightOptions[DEPARTING]?.flight.arrivalTime));
  const isContinueDisabled =
    flightsQuery.loading ||
    (searchCriteria.tripType === tripTypeValues.ONEWAY &&
      !selectedFlightOptions[DEPARTING]) ||
    (searchCriteria.tripType === tripTypeValues.ROUNDTRIP &&
      (!!error ||
        !selectedFlightOptions[DEPARTING] ||
        !selectedFlightOptions[RETURNING] ||
        !displayReturningSelectedDate ||
        disableFlightIfSameDay));

  const noDepartingFlights =
    !departingQuery ||
    departingQuery.lowFareQuery.error ||
    !departingQuery.lowFareQuery.lowFares.length;
  const noReturningFlights =
    !returningQuery ||
    returningQuery.lowFareQuery.error ||
    !returningQuery.lowFareQuery.lowFares.length;

  const NoFlightsErrorPage = (
    <NoFlightsErrorMessage
      translations={{
        missingFlightsErrorTitle,
        missingFlightsErrorMessage,
      }}
      searchCriteria={searchCriteria}
    />
  );

  const loadingTimeOutInSeconds = 60;

  const isLoading = useCallback(() => {
    return flightsQuery.loading;
  }, [flightsQuery.loading]);

  const initialState = {
    isLoadingTimeOut: false,
  };

  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (transactionId) {
      window.sessionStorage.setItem('popupAlreadyShown', false);
    }
  }, [transactionId]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (isLoading()) {
        setState({ ...state, isLoadingTimeOut: true });
      }
    }, loadingTimeOutInSeconds * 1000);
    return () => clearTimeout(timeOut);
  }, [isLoading, state]);

  const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);

  useEffect(() => {
    if (
      isFirstPageLoad &&
      !marketQuery.loading &&
      !flightsQuery.loading &&
      !departingQuery.lowFareQuery.loading &&
      !returningQuery.lowFareQuery.loading &&
      !(isAuthenticated && userDataLoading)
    ) {
      setIsFirstPageLoad(false);
    }
  }, [
    isFirstPageLoad,
    marketQuery.loading,
    flightsQuery.loading,
    departingQuery.lowFareQuery.loading,
    returningQuery.lowFareQuery.loading,
    isAuthenticated,
    userDataLoading,
  ]);

  useEffect(() => {
    // Wait for user data to load if authenticated, otherwise proceed immediately
    const isWaitingForUserData = isAuthenticated && userDataLoading;
    
    if (!isPageViewTriggered && !isWaitingForUserData) {
      tracking.trackPageView('', 'page_view');
      setIsPageViewTriggered(true);
    }
    if (
      !isFirstPageLoad &&
      !flightsQueryError &&
      !noDepartingFlights &&
      flightSearchCriteria &&
      transactionId
    ) {
      tracking.trackViewSearchResults({
        ...flightSearchCriteria,
        leg: 'departing',
      });
    }
  }, [
    flightSearchCriteria,
    flightsQueryError,
    isFirstPageLoad,
    isPageViewTriggered,
    noDepartingFlights,
    transactionId,
    isAuthenticated,
    userDataLoading,
  ]);

  if (
    flightsQueryError ||
    state.isLoadingTimeOut ||
    isSelectFlightError ||
    (!departingQuery.loading &&
      (noDepartingFlights ||
        (isRoundTrip && noReturningFlights && noDepartingFlights)))
  ) {
    return NoFlightsErrorPage;
  }

  const getUpdatedQueryParams = () => {
    const { transactionId, seatIds, sectionId, ...rest } = router.query;
    return Object.keys(rest).length > 0
      ? `${new URLSearchParams(rest).toString()}`
      : '';
  };

  return (
    <>
      <FlightsPageHeader automationHook={FLIGHTS_PAGE.hook} />
      {isAuthenticated && showUserLoyaltyFareBanner && loyaltyFare?.discount ? (
        <FlightsPromoBanner
          loyaltyFareDiscountText={loyaltyFareDiscountHeading}
          discount={loyaltyFare?.discount}
        />
      ) : null}
      {!!selectStadiumShowsError && (
        <ErrorPopup
          error={selectStadiumShowsError}
          redirectTo={`/booking/${transactionId}/tickets?${getUpdatedQueryParams()}`}
          isPopupOpen={!!selectStadiumShowsError}
          hideCloseButton={true}
        />
      )}
      {!isFirstPageLoad ? (
        <>
          <SearchResults
            backupTabDate={backupTabDate}
            setBackupTabDate={setBackupTabDate}
            flightSearchCriteria={searchCriteria}
            invalidState={error}
            flightsWarning={warning}
            makeOneway={makeOneway}
            makeRoundtrip={makeRoundtripFunction}
            tabDates={tabDates}
            isOnewayWithRoundTripDiscount={isOnewayWithRoundTripDiscount}
            onewayToRoundtripSavedAmount={
              selectedFlightOptions?.[DEPARTING]?.incentiveSavedAmount ||
              onewayToRoundtripSavedAmount
            }
            roundtripDiscountValue={
              selectedFlightOptions?.[DEPARTING] &&
              selectedFlightOptions?.[RETURNING]
                ? selectedFlightOptions[DEPARTING].totalDiscountValue +
                  selectedFlightOptions[RETURNING].totalDiscountValue
                : roundtripDiscountValue
            }
            departingQuery={departingQuery}
            returningQuery={returningQuery}
            setDepartingTabs={setDepartingTabsAndValidate}
            setReturningTabs={setReturningTabsAndClearWarning}
            setReturningTabsWithoutClearingWarning={setReturningTabs}
            selectedFlightOptions={selectedFlightOptions}
            setDepartingFlightOption={setDepartingFlightOption}
            setReturningFlightOption={setReturningFlightOption}
            lastAvailableDates={lastAvailableDates}
            earliestAvailableDates={earliestAvailableDates}
            displayReturningSelectedDate={displayReturningSelectedDate}
            setDisplayReturningSelectedDate={setDisplayCalendarViewSelectedDate}
            shouldReturnFlightOptionBeNull={shouldReturnFlightOptionBeNull}
            setShouldReturnFlightOptionBeNull={
              setShouldReturnFlightOptionBeNull
            }
            warning={warning}
            deactivateWarning={deactivateWarning}
            setIsArrowClicked={setIsArrowClicked}
            loyaltyPointsValue={getLoyaltyPointsValue()}
            isRedemptionEnabledForNavitaire={
              loyaltySettings?.isRedemptionEnabledForNavitaire
            }
            googleAnalyticsData={googleAnalyticsData}
            selectedBundles={selectedBundles}
            onBundleSelect={handleBundleSelect}
            bundleOffers={bundleOffersList}
            allAncillariesList={allAncillariesList}
          />
          {flightsPageDestinationAdvert?.content && (
            <Box mb={6} position="relative">
              <Markdown
                source={flightsPageDestinationAdvert.content}
                customElements={{ DestinationAdvert }}
              />
            </Box>
          )}
          <PageFooter
            onSubmit={() => {
              const flightsUnchanged =
                selectedFlightEqualsToPersistedFlight(DEPARTING) &&
                selectedFlightEqualsToPersistedFlight(RETURNING);
              const bundlesUnchanged = selectedBundlesEqualToPersistedBundles();

              if (
                flightsUnchanged &&
                bundlesUnchanged &&
                !(isStadium && !isPersistedShow) &&
                recoverableError?.graphqlErrors?.[0] !== 'FARES_CHANGED'
              ) {
                setLastStepCompleted('FLIGHTS');
                navigateToNextStep();
                // if departing and returning flight have not changed,
                // then check if stadium seat has changed
              } else if (
                cart?.order &&
                recoverableError?.graphqlErrors?.[0] !== 'FARES_CHANGED' &&
                flightsUnchanged &&
                bundlesUnchanged
              ) {
                selectStadiumShows({ travelers: cart?.order?.travelers });
              } else {
                invalidateFlightSearchCriteriaCache &&
                  invalidateFlightSearchCriteriaCache();
                const departureFareName = getFareNameByLeg(DEPARTING);
                const returningFareName = get(
                  selectedFlightOptions[RETURNING],
                  'flight.id'
                )
                  ? getFareNameByLeg(RETURNING)
                  : null;

                const continueMutationVariables = {
                  departureFlightId: get(
                    selectedFlightOptions[DEPARTING],
                    'flight.id'
                  ),
                  returningFlightId: get(
                    selectedFlightOptions[RETURNING],
                    'flight.id'
                  ),
                  selectedBundles: {
                    outboundTier:
                      departureFareName === PREMIUM_FARE_NAME
                        ? NEW_FARE_TIER
                        : selectedBundles.outboundTier === NEW_FARE_TIER
                          ? null
                          : selectedBundles.outboundTier,
                    inboundTier:
                      returningFareName === PREMIUM_FARE_NAME
                        ? NEW_FARE_TIER
                        : selectedBundles.inboundTier === NEW_FARE_TIER
                          ? null
                          : selectedBundles.inboundTier,
                  },
                  departureFareName: departureFareName,
                  returningFareName: returningFareName,
                };
                continueMutationVariablesRef.current = continueMutationVariables;

                submitFlights({
                  variables: continueMutationVariables,
                });
              }
            }}
            isSkippable={false}
            isContinueDisabled={isContinueDisabled}
            loading={isContinueLoading}
            automationHook={FLIGHTS_PAGE.hook}
            disclaimer={
              <Disclaimer
                disclaimerHook={FLIGHTS_DISCLAIMER.hook}
                disclaimerText={flightsPageDisclaimerText}
                disclaimerPopupHook={FLIGHTS_DISCLAIMER_POPUP.hook}
                disclaimerButtonHook={FLIGHTS_DISCLAIMER_BUTTON.hook}
                popupWindowContent={taxesChargesFeesPopupContent}
                popupWindowTitle={flightsPageDisclaimerPopupWindowTitle}
                disclaimerExternalLinkHook={
                  FLIGHTS_DISCLAIMER_EXTERNAL_LINK.hook
                }
                trackingLabel={'Taxes, carrier charges & government fees'}
                trackingAction={'Flight Chooser'}
                openExternalWindowTrackingLabel={'baggage charges'}
              />
            }
          />
          <VerticalSpacing size={mq.lte('sm') ? 6 : 10} />
        </>
      ) : (
        <SpinnerOverlay automationHook={FLIGHTS_PAGE_SPINNER.hook} />
      )}
    </>
  );
};

IndexPage.propTypes = propTypes;

const IndexPageWrapper = () => {
  const { loading, error, data, invalidateFlightSearchCriteriaCache } =
    useFlightSearchCriteria();

  if (error) {
    return <ExpiredTransactionIdErrorPage />;
  }

  return (
    <>
      {!loading && (
        <IndexPage
          flightSearchCriteria={data}
          invalidateFlightSearchCriteriaCache={
            invalidateFlightSearchCriteriaCache
          }
        />
      )}
    </>
  );
};

export default withTranslations(
  FLIGHTS_PAGE_TRANSLATIONS_QUERY,
  IndexPageWrapper
);
